<template>
  <div class="admin-list-page location-directory">
    <div class="admin-page-heading mb-5">
      <div>
        <div class="text-overline primary--text font-weight-bold">Locations</div>
        <h1 class="text-h4 font-weight-bold mb-1">Location directory</h1>
        <p class="admin-muted mb-0">{{ count }} location{{ count === 1 ? '' : 's' }} in {{ currentOrganization.name }}</p>
      </div>
      <v-btn color="primary" depressed rounded :to="{ name: 'location-create' }">
        <v-icon left>mdi-store-plus-outline</v-icon>Add location
      </v-btn>
    </div>

    <v-card outlined class="admin-panel rounded-xl">
      <v-card-text class="pa-4 pa-sm-6">
        <div class="location-directory__tools mb-6">
          <v-text-field
            v-model="searchInput"
            aria-label="Search locations"
            autocomplete="off"
            clearable dense outlined rounded hide-details
            prepend-inner-icon="mdi-magnify"
            placeholder="Search by name, code, address, or city"
            class="admin-list-search"
            @keydown.enter="applySearch"
            @click:clear="clearSearch"
          />
          <v-btn color="primary" depressed rounded @click="applySearch">Search</v-btn>
        </div>

        <v-alert v-if="errorMessage" type="error" text class="rounded-lg mb-5">
          <div class="d-flex flex-wrap align-center justify-space-between">
            <span>{{ errorMessage }}</span>
            <v-btn text color="error" @click="getList">Try again</v-btn>
          </div>
        </v-alert>

        <v-row v-if="loading">
          <v-col v-for="index in 6" :key="index" cols="12" sm="6" lg="4">
            <v-skeleton-loader type="card-heading, list-item-two-line, actions" class="rounded-xl" />
          </v-col>
        </v-row>

        <v-row v-else-if="locations.length">
          <v-col v-for="location in locations" :key="location.id" cols="12" sm="6" lg="4">
            <v-card outlined class="admin-location-card location-directory-card rounded-xl" height="100%">
              <v-card-title class="align-start pa-5 pb-3">
                <v-avatar color="primary" size="54" class="admin-location-card__icon location-directory-card__logo mr-3">
                  <v-img
                    v-if="location.logoURL && !brokenLogos[location.id]"
                    :src="location.logoURL"
                    :alt="`${location.name} logo`"
                    contain
                    @error="markLogoBroken(location.id)"
                  />
                  <v-icon v-else dark aria-hidden="true">mdi-store-marker-outline</v-icon>
                </v-avatar>
                <div class="min-width-0 flex-grow-1">
                  <div class="text-subtitle-1 font-weight-bold text-truncate" v-html="$options.filters.highlight(location.name, search)" />
                  <div class="text-caption admin-muted text-truncate">{{ currentOrganization.name }}</div>
                </div>
              </v-card-title>

              <v-card-text class="px-5 py-2 location-directory-card__details">
                <div class="location-code-row mb-2">
                  <span class="location-code-row__label">Register code</span>
                  <code
                    class="location-code-row__value"
                    v-html="$options.filters.highlight(location.code || 'Not assigned', search)"
                  />
                </div>
                <div class="location-detail-row">
                  <v-icon small class="mr-3">mdi-map-marker-outline</v-icon>
                  <span v-if="addressText(location)" v-html="$options.filters.highlight(addressText(location), search)" />
                  <span v-else class="admin-muted">No address provided</span>
                </div>
                <div class="location-detail-row">
                  <v-icon small class="mr-3">mdi-phone-outline</v-icon>
                  <span v-if="phoneNumber(location)" v-html="$options.filters.highlight(phoneNumber(location), search)" />
                  <span v-else class="admin-muted">No phone number provided</span>
                </div>
              </v-card-text>

              <v-spacer />
              <v-divider />
              <v-card-actions class="location-directory-card__actions pa-3">
                <v-btn
                  text
                  color="primary"
                  block
                  :aria-label="`View ${location.name || 'location'}`"
                  :to="{ name: 'location-profile', params: { id: location.id } }"
                >
                  <v-icon left>mdi-eye-outline</v-icon>View location
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

        <div v-else-if="!errorMessage" class="admin-empty-state py-12 text-center">
          <v-icon size="56" color="primary">mdi-store-search-outline</v-icon>
          <h2 class="text-h6 mt-3">{{ search ? 'No matching locations' : 'No locations yet' }}</h2>
          <p class="admin-muted mb-4">{{ search ? 'Try a different name, code, address, or city.' : 'Add your first location to get started.' }}</p>
          <v-btn v-if="search" outlined rounded color="primary" @click="clearSearch">Clear search</v-btn>
          <v-btn v-else color="primary" depressed rounded :to="{ name: 'location-create' }">Add location</v-btn>
        </div>

        <div v-if="!loading && count > options.itemsPerPage" class="location-directory__pagination mt-6">
          <div class="text-caption admin-muted text-center mb-2">{{ pageRange }} of {{ count }}</div>
          <v-pagination v-model="options.page" :length="pageCount" :total-visible="$vuetify.breakpoint.xs ? 5 : 7" circle color="primary" aria-label="Location pages" />
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import locationService from "../service.js";

export default {
  name: "location-list",
  data() {
    return {
      search: "",
      searchInput: "",
      locations: [],
      count: 0,
      loading: false,
      errorMessage: "",
      brokenLogos: {},
      options: { page: 1, itemsPerPage: 12, sortBy: ["name"], sortDesc: [false] },
    };
  },
  computed: {
    ...mapGetters("global", ["currentOrganization"]),
    pageCount() { return Math.ceil(this.count / this.options.itemsPerPage); },
    pageRange() {
      if (!this.count) return "0";
      const start = (this.options.page - 1) * this.options.itemsPerPage + 1;
      return `${start}–${Math.min(start + this.locations.length - 1, this.count)}`;
    },
  },
  watch: {
    options: {
      handler() { this.getList(); },
      deep: true,
    },
  },
  created() { this.getList(); },
  methods: {
    applySearch() {
      this.search = String(this.searchInput || "").trim();
      if (this.options.page !== 1) this.options.page = 1;
      else this.getList();
    },
    clearSearch() {
      this.searchInput = "";
      this.search = "";
      if (this.options.page !== 1) this.options.page = 1;
      else this.getList();
    },
    addressText(location) {
      const address = location.Address || {};
      const state = address.State && (address.State.name || address.State.code);
      return [address.addressLine1, address.addressLine2, address.city, state, address.postalCode]
        .filter(Boolean).join(", ");
    },
    phoneNumber(location) { return location.Address && location.Address.phoneNumber; },
    markLogoBroken(locationId) { this.$set(this.brokenLogos, locationId, true); },
    async getList() {
      this.loading = true;
      this.errorMessage = "";
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      try {
        const response = await locationService.getAll({
          limit: itemsPerPage,
          offset: (page - 1) * itemsPerPage,
          search: this.search,
          orderBy: [sortBy[0] || "name", sortDesc[0] ? "DESC" : "ASC"],
        });
        this.locations = response.data || [];
        this.count = response.count || 0;
      } catch (error) {
        this.locations = [];
        this.count = 0;
        this.errorMessage = "Locations could not be loaded. Please try again.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.location-directory__tools { display: flex; align-items: center; gap: 12px; }
.location-directory-card { display: flex; flex-direction: column; min-height: 260px; }
.location-directory-card__logo { overflow: hidden; }
.location-directory-card__logo .v-image { background: white; }
.location-directory-card__details { flex: 1 1 auto; }
.location-code-row {
  align-items: center;
  background: var(--brand-bg);
  border-radius: 10px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-height: 44px;
  padding: 8px 12px;
  width: 100%;
}
.location-code-row__label { color: rgba(0, 0, 0, 0.62); font-size: 0.75rem; font-weight: 600; }
.location-code-row__value { background: transparent; color: inherit; font-size: 0.86rem; font-weight: 700; overflow-wrap: anywhere; text-align: right; }
.location-detail-row { display: flex; align-items: flex-start; min-height: 44px; padding: 9px 0; line-height: 1.5; }
@media (max-width: 599px) {
  .location-directory__tools { align-items: stretch; flex-direction: column; }
  .location-directory__tools .v-btn { width: 100%; }
  .location-directory-card__actions { display: block; }
  .location-directory-card__actions .v-btn { margin: 0; min-height: 44px; }
  .location-code-row { align-items: flex-start; flex-direction: column; gap: 2px; }
  .location-code-row__value { text-align: left; width: 100%; }
}
</style>
